import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import FormTextField from 'components/formComponents/FormTextField';
import {
  IPaymentTypes,
  NewSubscriptionParser,
} from 'pages/newSubscription/models';
import { NewSubscriptionApi } from 'api/newSubscriptionApi';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectPrice,
  selectRegistrationToken,
  selectSelectedPackages,
  selectSchoolAccountContext,
  setPoNumber,
  setFinanceEmail,
  setSecondUserDetails,
  setSecondExistingUser,
  setAddOrUpgradeUserDetails,
  selectMembershipOptInfo,
} from 'reducers/misc';
import { useLocation } from '@reach/router';
import { AppPath } from 'appConstants';
import { selectSignedIn } from 'reducers/auth';
import { ThreeBounce } from 'better-react-spinkit';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import { formatNumber, formatPrice } from 'utils/formating';
import { SubscribeApi } from 'api/subscribeApi';
import { PricingSummaryDetails } from 'components/pricingSummaryDetails/PricingSummaryDetails';
import { SubscriptionsApi } from 'api/subscriptionsApi';
import { selectQuotesId, selectQuotePromoCodeSlots } from 'reducers/misc';
import { PackagePromo } from 'subscriptionOptions/types';

type PaymentTypeFormProps = {
  onSubmit: (typeOfPayment: 'card' | 'invoice') => void | null;
};

export const PaymentTypeForm: React.FC<PaymentTypeFormProps> = ({
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const pathname = location.pathname;

  const [error, setError] = useState<string>('');
  const [validationSchema, setValidationSchema] = useState<any>(null);
  const [addSecondUserType, setAddSecondUserType] = useState<any>('');
  const [suTypeError, setSuTypeError] = useState<any>('');
  const [existingUserError, setExistingUserError] = useState<any>('');

  const selectedPackages = useSelector(selectSelectedPackages);
  const price = useSelector(selectPrice);
  const registrationToken = useSelector(selectRegistrationToken);
  const existingMembershipOptInfo = useSelector(selectMembershipOptInfo);
  const quotesId: number | null = useSelector(selectQuotesId);
  const promoCodeSlots = useSelector(selectQuotePromoCodeSlots);

  /** The pairs the summary priced, so payment sends what produced the amount shown. */
  const [packagePromos, setPackagePromos] = useState<PackagePromo[]>([]);

  const quotePackages =
    existingMembershipOptInfo && existingMembershipOptInfo[0]
      ? existingMembershipOptInfo[0].quotePackages
      : null;

  // Signed-in add flow only: the package step loaded this. Other flows keep the unfetched defaults.
  const isSignedIn = useSelector(selectSignedIn);
  const storedAccountContext = useSelector(selectSchoolAccountContext);
  const schoolAccountContext = isSignedIn && pathname === AppPath.NEW_SUBSCRIPTION ? storedAccountContext : null;
  const financeEmail = schoolAccountContext ? schoolAccountContext.financeEmail || '' : '';
  const addSecondSchoolUser = schoolAccountContext ? schoolAccountContext.bAddSecondSchoolUser : null;
  const chooseAddOrUpgrade = schoolAccountContext ? schoolAccountContext.bChooseAddOrUpgrade : null;
  const schoolUsersToUpgrade = schoolAccountContext ? schoolAccountContext.schoolUsersToUpgrade : null;
  const anyFreeTrialSub = schoolAccountContext ? schoolAccountContext.bAnyFreeTrialSubscription : false;
  const anyFullPaidSub = schoolAccountContext ? schoolAccountContext.bAnyFullPaidSubscription : false;

  const [anyFreeTrialSubFlag, setAnyFreeTrialSubFlag] = useState<boolean>(false);
  const [anyFullPaidSubFlag, setAnyFullPaidSubFlag] = useState<boolean>(false);   

  useEffect(() => {
    setAnyFreeTrialSubFlag(anyFreeTrialSub);
    setAnyFullPaidSubFlag(anyFullPaidSub);
  }, [anyFreeTrialSub, anyFullPaidSub]);     

  const secondSchoolUserValidationSchema = Yup.object({
      FinanceEmail: Yup.string()
        .required('Please enter an email')
        .email('Please enter a valid email'),
      firstName: Yup.string().required('Please enter a first name'),
      lastName: Yup.string().required('Please enter a last name'),
      emailAddress: Yup.string()
        .required('Please enter your email')
        .email('Please enter a valid email'),   
    });

  const regularValidationSchema = Yup.object({
    FinanceEmail: Yup.string()
      .required('Please enter an email')
      .email('Please enter a valid email'),
  });  

  useEffect(() => {
    const selectedSchema = 
      (addSecondSchoolUser && !chooseAddOrUpgrade) ?
        secondSchoolUserValidationSchema :
        regularValidationSchema;

    setValidationSchema(selectedSchema);
  }, [addSecondSchoolUser, chooseAddOrUpgrade]);

  const btnRevealCheck = (values: any) => {
    if(
      (addSecondSchoolUser && !chooseAddOrUpgrade) ||
      (!addSecondSchoolUser && chooseAddOrUpgrade && addSecondUserType === "manual")
    ){
      return (
        !values.byCard && 
        !values.byInvoice
      )
      || values.FinanceEmail === ""
      || values.firstName === ""
      || values.lastName === ""
      || values.emailAddress === "";
    } else if (!addSecondSchoolUser && chooseAddOrUpgrade && addSecondUserType === "existing") {
      return (
        !values.byCard && 
        !values.byInvoice
      ) 
      || values.FinanceEmail === "" 
      || !values.secondUserId 
      || values.secondUserId === "-1";
    } else {
      return (!values.byCard && !values.byInvoice) || values.FinanceEmail === "";
    }
  };

  const subscribeByInvoice = async (
    setSubmitting: (status: boolean) => void,
    poNumber: string,
    FinanceEmail: string,
    values: any,
    addSecondSchoolUser: boolean | null,
    chooseAddOrUpgrade: boolean | null,
    addSecondUserType: string,
    setErrors: any,
  ) => {
    // Never pay before the summary has priced: the promos it sends are the ones it priced.
    if (packagePromos.length === 0) {
      setSubmitting(false);
      setError('Sorry, something went wrong. Please try again.');
      return;
    }

    setSubmitting(true);
    let result;
    if (AppPath.SUBSCRIBE === pathname) {
      result = await SubscribeApi.subscribeByInvoice(
        quotesId,
        registrationToken,
        packagePromos,
        price.total,
        poNumber,
        FinanceEmail,
      );
    } else {
      result = await NewSubscriptionApi.subscribeByInvoice(
        packagePromos,
        price.total,
        poNumber,
        FinanceEmail,
        values,
        addSecondSchoolUser,
        chooseAddOrUpgrade,
        addSecondUserType
      );
    }
    if (result.data) {
      if(AppPath.SUBSCRIBE !== pathname){
        checkFreeTrialAvailable();
      }

      onSubmit('invoice');
    }
    if (result.error) {
      if(result.error.message === "This user already exists."){
        setErrors({ emailAddress: result.error.message });
        setError("");
      } else {
        setError(result.error.message);
      }
    }
    setSubmitting(false);
  };

  const switchSecondUserMethod = (selected_method: string) => {
    if(selected_method === "manual"){
      setValidationSchema(secondSchoolUserValidationSchema);
    } else {
      setValidationSchema(regularValidationSchema);
    }

    setSuTypeError("");
    setAddSecondUserType(selected_method);
  };

  const handleSubmitForm = (
    values: any,
    { setSubmitting, setErrors }: FormikHelpers<any>
  ) => {
    if (!values.byCard && !values.byInvoice) return;

    if (!addSecondSchoolUser && chooseAddOrUpgrade && !addSecondUserType) {
      setSuTypeError("Please select a method to add a second user");
      setSubmitting(false);
      return;
    }

    if (
      !addSecondSchoolUser && 
      chooseAddOrUpgrade && 
      addSecondUserType === "existing" &&
      (!values.secondUserId || values.secondUserId === "-1")
    ) {
      setExistingUserError("Please select an existing user");
      setSubmitting(false);
      return;      
    }

    const { poNumber, FinanceEmail } = values;

    if (values.byCard) {
      if(
        (addSecondSchoolUser && !chooseAddOrUpgrade) ||
        (!addSecondSchoolUser && chooseAddOrUpgrade && addSecondUserType === "manual")
      ){
        const { 
          firstName, 
          lastName, 
          emailAddress, 
        } = values;

        dispatch(setSecondUserDetails({
          firstName,
          lastName,
          emailAddress,
        }));
      }

      if(
        (addSecondSchoolUser && !chooseAddOrUpgrade) ||
        (!addSecondSchoolUser && chooseAddOrUpgrade && addSecondUserType === "existing")        
      ){
        dispatch(setSecondExistingUser({
          mainAccountHolderSchoolUsersId: parseInt(values.secondUserId)
        }));
      }

      if(
        chooseAddOrUpgrade !== undefined &&
        chooseAddOrUpgrade !== null && 
        addSecondSchoolUser !== undefined &&
        addSecondSchoolUser !== null
      ){
        dispatch(setAddOrUpgradeUserDetails({
          chooseAddOrUpgrade,
          addSecondSchoolUser
        }));
      }

      dispatch(setPoNumber(poNumber));
      dispatch(setFinanceEmail(FinanceEmail));
      setSubmitting(true);
      onSubmit('card');
    }

    if (values.byInvoice) {
      subscribeByInvoice(
        setSubmitting, 
        poNumber, 
        FinanceEmail, 
        values, 
        addSecondSchoolUser,
        chooseAddOrUpgrade,
        addSecondUserType,
        setErrors,
      );
    }
  };

  const checkFreeTrialAvailable = async () => {
    const result = await SubscriptionsApi.getSubscriptionsList();

    if (result.data) {
      const { bCanAddFreeTrial } = result.data;

      if(
        !bCanAddFreeTrial &&
        document.getElementsByClassName("HeaderDropdownMenu")[0] &&
        document.getElementsByClassName("HeaderDropdownMenu")[0].getElementsByClassName("LinkItem UpgradeLink")[0]
      ){
        document.getElementsByClassName("HeaderDropdownMenu")[0].getElementsByClassName("LinkItem UpgradeLink")[0].remove();
      }
    }
  };    

  return (
    <div className="Form PaymentTypeForm">
      <Formik
        initialValues={NewSubscriptionParser.createEmptyPaymentType(financeEmail)}
        onSubmit={(values, formikHelpers) =>
          handleSubmitForm(values, formikHelpers)
        }
        validationSchema={validationSchema}
        enableReinitialize={true}
      >
        {({ isSubmitting, values, setFieldValue }) => {
          return (
            <Form>
              <div className="PricingColRow">
                <div className="PCRowInner">
                  <div className="LeftCol">
                    <FormGeneralError message={error} />
                    <FormTextField
                      field={'poNumber'}
                      placeholder={'Enter PO number (optional)'}
                      required={false}
                    />
                    <div className="PayTypeOptRow">
                      <label className={'PaymentTypeOption'}>
                        <input
                          type="radio"
                          name="payment-type"
                          className="HiddenInput"
                          checked={values.byCard}
                          onChange={() => {
                            setFieldValue('byCard', true);
                            setFieldValue('byInvoice', false);
                          }}
                        />
                        <label className="CustomInput" htmlFor={'byCard'}>
                          Pay by card
                          <span>£{formatPrice(price.total)}</span>
                        </label>
                      </label>                  
                      <label className={'PaymentTypeOption'}>
                        <input
                          type="radio"
                          name="payment-type"
                          className="HiddenInput"
                          checked={values.byInvoice}
                          onChange={() => {
                            setFieldValue('byInvoice', true);
                            setFieldValue('byCard', false);
                          }}
                        />
                        <label className="CustomInput" htmlFor={'byInvoice'}>
                          Pay by invoice
                          <span>£{formatPrice(price.total)}</span>
                        </label>
                      </label>
                    </div>

                    <p>Enter email address to send card receipt / invoice to:</p>
                    <div className={"FinanceEmailField" + ((values.byInvoice || values.byCard) ? " HighlightFEField" : "")}>
                      <FormTextField
                        field={'FinanceEmail'}
                        placeholder={'Email address'}
                        required={true}
                        value={values.FinanceEmail}
                      />
                    </div>

                    {(addSecondSchoolUser || chooseAddOrUpgrade) &&
                      <div className="AddSecondUserFields">
                        <h4>Add second user</h4>
                        <div className="LineSpacer2"></div>

                        {!addSecondSchoolUser && chooseAddOrUpgrade &&
                          <>
                            <div className="RadioButtons">
                              <div
                                className={"Radio" + (suTypeError ? " ErrorRadio" : "")}
                                role="checkbox"
                                aria-checked={addSecondUserType === "manual"}
                                onClick={() => switchSecondUserMethod("manual")}
                              >
                                <input
                                  id={"secondUserManual"}
                                  type="radio"
                                  name={"secondUserSelection"}
                                  value={"manual"}
                                  checked={addSecondUserType === "manual"}
                                  onChange={() => {}}
                                />
                                <label className="CustomInput" htmlFor={"secondUserManual"}>
                                  Manual
                                </label>
                              </div>
                              <div
                                className={"Radio" + (suTypeError ? " ErrorRadio" : "")}
                                role="checkbox"
                                aria-checked={addSecondUserType === "existing"}
                                onClick={() => switchSecondUserMethod("existing")}
                              >
                                <input
                                  id={"secondUserExisting"}
                                  type="radio"
                                  name={"secondUserSelection"}
                                  value={"existing"}
                                  checked={addSecondUserType === "existing"}
                                  onChange={() => {}}
                                />
                                <label className="CustomInput" htmlFor={"secondUserExisting"}>
                                  Existing
                                </label>
                              </div>
                            </div>                        
                            {suTypeError && <div className="ErrorLabel">{suTypeError}</div>}
                          </>                      
                        }
                      </div>
                    }

                    {
                      ((addSecondSchoolUser && !chooseAddOrUpgrade) ||
                      (!addSecondSchoolUser && chooseAddOrUpgrade && addSecondUserType === "manual")) &&
                      <>
                        <div className="SideBySide">
                          <FormTextField
                            field={'firstName'}
                            label={'First Name'}
                            required={true}
                            value={values.firstName}
                          />
                          <FormTextField
                            field={'lastName'}
                            label={'Last Name'}
                            required={true}
                            value={values.lastName}
                          />                       
                        </div>
                        <FormTextField
                          field={'emailAddress'}
                          label={'Email address'}
                          required={true}
                          value={values.emailAddress}
                        />                    
                      </>
                    }

                    {!addSecondSchoolUser && chooseAddOrUpgrade && addSecondUserType === "existing" && schoolUsersToUpgrade &&
                      <>
                        <select 
                          name="secondUserId" 
                          className={"DropDown PTDD" + (existingUserError ? " ErrorDropdown" : "")} 
                          onChange={(e) => {
                            setExistingUserError("");
                            setFieldValue("secondUserId", e.target.value);
                          }}
                        >
                            <option value="-1">Please select an existing user</option>
                            {schoolUsersToUpgrade.map((item: any) => {
                              return (
                                <option value={item.schoolUsersId}>{item.schoolUsersFullName}</option>
                              );
                            })}
                        </select> 
                        {existingUserError && <div className="ErrorLabel">{existingUserError}</div>}
                      </>
                    }                 
                  </div>
                  <div className="RightCol">
                    <PricingSummaryDetails
                      addPromoStep={false}
                      isDisabled={isSubmitting || btnRevealCheck(values)}
                      isSubmitting={isSubmitting}
                      btnText="Save and continue"
                      anyFullPaidSub={anyFullPaidSubFlag}
                      existingMembershipOptInfo={existingMembershipOptInfo}
                      accountContext={schoolAccountContext}
                      onPricedPromos={setPackagePromos}
                      {...(quotePackages && quotePackages.length > 0
                        ? { promoCodeSlots, quotePackages }
                        : {})}
                    />
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
